import { useState, useEffect } from 'react'
import { supabase } from '../../supabase'
import { formatEuro, MOIS_LABELS } from '../../utils/calculs'
import { getAnneeScolaireEnCours, labelAnneeScolaire, plageAnneeScolaire, listeAnneesScolaires } from '../../utils/anneeScolaire'

export default function RecapAnnuel() {
  const [annee, setAnnee] = useState(getAnneeScolaireEnCours())
  const [pole, setPole] = useState('SPA')
  const [data, setData] = useState([])
  const [superviseurs, setSuperviseurs] = useState([])
  const [financier, setFinancier] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => { load() }, [annee, pole]) // eslint-disable-line react-hooks/exhaustive-deps

  async function load() {
    setLoading(true)
    const { debut, fin } = plageAnneeScolaire(annee)

    const [{ data: seances }, { data: sups }, { data: saisies }] = await Promise.all([
      supabase.from('seances')
        .select('date, pole, ca_ht, ca_ttc, remuneration_reelle, remuneration_affichee, total_massage_payant, total_massage_gratuit, superviseur_id, superviseur2_id')
        .eq('pole', pole).gte('date', debut).lte('date', fin),
      supabase.from('profiles')
        .select('id, prenom, nom').eq('role', 'superviseur').order('prenom'),
      supabase.from('saisies_financieres')
        .select('date_debut, cb_pin, cb_sans_contact, cheque_comptant, cheque_differe, especes')
        .gte('date_debut', debut).lte('date_debut', fin)
    ])

    setSuperviseurs(sups || [])
    setData(seances || [])
    setFinancier(saisies || [])
    setLoading(false)
  }

  // Mois de l'année scolaire : sept (9) → août (8)
  const moisScolaires = [9,10,11,12,1,2,3,4,5,6,7,8]

  // Encaissements agrégés par mois (depuis date_debut)
  const encaisseParMois = {}
  for (const m of moisScolaires) encaisseParMois[m] = 0
  for (const s of financier) {
    const m = new Date(s.date_debut).getMonth() + 1
    encaisseParMois[m] += (s.cb_pin || 0) + (s.cb_sans_contact || 0) + (s.cheque_comptant || 0) + (s.cheque_differe || 0) + (s.especes || 0)
  }

  // Construire la grille par superviseur x par mois
  const grid = {}
  for (const sup of superviseurs) {
    grid[sup.id] = { superviseur: sup, mois: {}, total: { ca_ttc: 0, rem_reelle: 0, rem_affichee: 0, seances: 0 } }
    for (const m of moisScolaires) grid[sup.id].mois[m] = { ca_ttc: 0, rem_reelle: 0, rem_affichee: 0, seances: 0 }
  }

  for (const s of data) {
    const mois = new Date(s.date).getMonth() + 1
    const ids = [s.superviseur_id, s.superviseur2_id].filter(Boolean)
    for (const id of ids) {
      if (!grid[id]) continue
      grid[id].mois[mois].ca_ttc += s.ca_ttc || 0
      grid[id].mois[mois].rem_reelle += s.remuneration_reelle || 0
      grid[id].mois[mois].rem_affichee += s.remuneration_affichee || 0
      grid[id].mois[mois].seances += 1
      grid[id].total.ca_ttc += s.ca_ttc || 0
      grid[id].total.rem_reelle += s.remuneration_reelle || 0
      grid[id].total.rem_affichee += s.remuneration_affichee || 0
      grid[id].total.seances += 1
    }
  }

  // Totaux par mois
  const totauxMois = {}
  for (const m of moisScolaires) {
    totauxMois[m] = { ca_ttc: 0, rem_reelle: 0, rem_affichee: 0, seances: 0 }
    for (const row of Object.values(grid)) {
      totauxMois[m].ca_ttc += row.mois[m].ca_ttc
      totauxMois[m].rem_reelle += row.mois[m].rem_reelle
      totauxMois[m].rem_affichee += row.mois[m].rem_affichee
      totauxMois[m].seances += row.mois[m].seances
    }
  }

  const totauxAnnee = {
    ca_ttc: data.reduce((a, s) => a + (s.ca_ttc || 0), 0),
    rem_reelle: data.reduce((a, s) => a + (s.remuneration_reelle || 0), 0),
    rem_affichee: data.reduce((a, s) => a + (s.remuneration_affichee || 0), 0),
    seances: data.length,
    massages: data.reduce((a, s) => a + (s.total_massage_payant || 0) + (s.total_massage_gratuit || 0), 0),
    encaisse: financier.reduce((a, s) => a + (s.cb_pin || 0) + (s.cb_sans_contact || 0) + (s.cheque_comptant || 0) + (s.cheque_differe || 0) + (s.especes || 0), 0),
  }

  const moisActifs = moisScolaires.filter(m => Object.values(grid).some(r => r.mois[m].seances > 0) || encaisseParMois[m] > 0)

  // Label du mois avec l'année correcte
  function labelMois(m) {
    const a = m >= 9 ? annee : annee + 1
    return `${MOIS_LABELS[m].slice(0,3)} ${String(a).slice(2)}`
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h2>Récapitulatif annuel</h2>
          <div className="page-header-sub">Année scolaire {labelAnneeScolaire(annee)}</div>
        </div>
      </div>

      <div style={{display:'flex', gap:12, marginBottom:24, flexWrap:'wrap'}}>
        <select value={annee} onChange={e => setAnnee(+e.target.value)}
          style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
          {listeAnneesScolaires().map(a => <option key={a} value={a}>{labelAnneeScolaire(a)}</option>)}
        </select>
        <select value={pole} onChange={e => setPole(e.target.value)}
          style={{padding:'8px 12px', border:'1px solid var(--border)', borderRadius:'var(--radius)'}}>
          <option value="SPA">SPA</option>
          <option value="HYDRO">Hydrothérapie</option>
        </select>
      </div>

      <div className="stats-grid" style={{marginBottom:24}}>
        <div className="stat-card">
          <div className="s-label">CA TTC massages {labelAnneeScolaire(annee)}</div>
          <div className="s-value">{formatEuro(totauxAnnee.ca_ttc)}</div>
        </div>
        <div className="stat-card">
          <div className="s-label">CA encaissé TTC</div>
          <div className="s-value">{formatEuro(totauxAnnee.encaisse)}</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Rém. réelle</div>
          <div className="s-value">{formatEuro(totauxAnnee.rem_reelle)}</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Rém. à facturer</div>
          <div className="s-value text-bordeaux">{formatEuro(totauxAnnee.rem_affichee)}</div>
        </div>
        <div className="stat-card">
          <div className="s-label">Jours</div>
          <div className="s-value">{totauxAnnee.seances}</div>
          <div className="s-sub">{totauxAnnee.massages} massages</div>
        </div>
      </div>

      {loading ? <div className="spinner" /> : (
        <>
          <div className="card" style={{overflowX:'auto', marginBottom:20}}>
            <h3 style={{marginBottom:16}}>Rémunérations par superviseur</h3>
            <table>
              <thead>
                <tr>
                  <th>Superviseur</th>
                  {moisActifs.map(m => <th key={m} className="num" style={{fontSize:10}}>{labelMois(m)}</th>)}
                  <th className="num">Total rém.</th>
                  <th className="num">Rém. réelle</th>
                  <th className="num">Jours</th>
                </tr>
              </thead>
              <tbody>
                {Object.values(grid).filter(r => r.total.seances > 0).map(row => (
                  <tr key={row.superviseur.id}>
                    <td style={{whiteSpace:'nowrap'}}><strong>{row.superviseur.prenom} {row.superviseur.nom}</strong></td>
                    {moisActifs.map(m => (
                      <td key={m} className="num">
                        {row.mois[m].seances > 0
                          ? <span style={{color:'var(--bordeaux)'}}>{formatEuro(row.mois[m].rem_affichee)}</span>
                          : <span className="text-muted">—</span>}
                      </td>
                    ))}
                    <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(row.total.rem_affichee)}</strong></td>
                    <td className="num text-muted">{formatEuro(row.total.rem_reelle)}</td>
                    <td className="num">{row.total.seances}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total</strong></td>
                  {moisActifs.map(m => (
                    <td key={m} className="num">
                      {totauxMois[m].seances > 0
                        ? <strong>{formatEuro(totauxMois[m].rem_affichee)}</strong>
                        : <span className="text-muted">—</span>}
                    </td>
                  ))}
                  <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(totauxAnnee.rem_affichee)}</strong></td>
                  <td className="num text-muted"><strong>{formatEuro(totauxAnnee.rem_reelle)}</strong></td>
                  <td className="num"><strong>{totauxAnnee.seances}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="card" style={{overflowX:'auto'}}>
            <h3 style={{marginBottom:16}}>CA et encaissements par mois</h3>
            <table>
              <thead>
                <tr>
                  <th>Mois</th>
                  <th className="num">CA TTC massages</th>
                  <th className="num">CA encaissé TTC</th>
                  <th className="num">Jours</th>
                  <th className="num">Rém. facturée</th>
                </tr>
              </thead>
              <tbody>
                {moisActifs.map(m => (
                  <tr key={m}>
                    <td>{MOIS_LABELS[m]} {m >= 9 ? annee : annee + 1}</td>
                    <td className="num">{formatEuro(totauxMois[m].ca_ttc)}</td>
                    <td className="num">{encaisseParMois[m] > 0 ? formatEuro(encaisseParMois[m]) : <span className="text-muted">—</span>}</td>
                    <td className="num">{totauxMois[m].seances || <span className="text-muted">—</span>}</td>
                    <td className="num" style={{color:'var(--bordeaux)'}}>{formatEuro(totauxMois[m].rem_affichee)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td><strong>Total {labelAnneeScolaire(annee)}</strong></td>
                  <td className="num"><strong>{formatEuro(totauxAnnee.ca_ttc)}</strong></td>
                  <td className="num"><strong>{formatEuro(totauxAnnee.encaisse)}</strong></td>
                  <td className="num"><strong>{totauxAnnee.seances}</strong></td>
                  <td className="num"><strong style={{color:'var(--bordeaux)'}}>{formatEuro(totauxAnnee.rem_affichee)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
